import { LightningElement, api } from 'lwc';

import { fetchAuthSession } from 'aws-amplify/auth';
import { get } from 'aws-amplify/api';

import { IS_TESTING_NO_INTERNET } from '../../../utils/basicUtils';

const PAGE_SIZE_OPTIONS = [
    { label: '10', value: '10' },
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' }
];

const EMPTY_VALUE = '-';

function parseDateTime(value) {
    if (!value) return null;

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateTime(value) {
    const date = parseDateTime(value);

    if (!date) return EMPTY_VALUE;

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
    }).format(date);
}

function formatCurrencyFromCents(value) {
    const cents = Number(value);

    if (!Number.isFinite(cents)) return EMPTY_VALUE;

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(cents / 100);
}

function formatValue(value) {
    if (value === null || value === undefined || value === '') {
        return EMPTY_VALUE;
    }

    return String(value);
}

function createSampleReservations() {
    const now = new Date();
    const future = new Date(now);
    future.setDate(now.getDate() + 3);
    future.setHours(13, 0, 0, 0);

    const past = new Date(now);
    past.setDate(now.getDate() - 12);
    past.setHours(10, 30, 0, 0);

    return {
        upcomingReservations: [
            {
                id: 'sample-upcoming-1',
                start_time: future.toISOString(),
                end_time: new Date(future.getTime() + 2 * 60 * 60 * 1000).toISOString(),
                guest_count: 4,
                host_payout_cents: 11200,
                payment_status: 'paid',
                refunded_cents: 0,
                app_user: {
                    first_name: 'Avery',
                    last_name: 'Stone'
                }
            }
        ],
        pastReservations: [
            {
                id: 'sample-past-1',
                start_time: past.toISOString(),
                end_time: new Date(past.getTime() + 3 * 60 * 60 * 1000).toISOString(),
                guest_count: 6,
                host_payout_cents: 16800,
                payment_status: 'paid',
                refunded_cents: 0,
                app_user: {
                    first_name: 'Jordan',
                    last_name: 'Lee'
                }
            }
        ],
        upcomingTotal: 1,
        pastTotal: 1
    };
}

export default class GetWetHostReservations extends LightningElement {
    _idToken;
    _hasConnected = false;
    _listingId;
    _listingName;
    _isLoading = false;
    toastObj;

    pageSize = 10;
    upcomingPage = 1;
    pastPage = 1;
    upcomingTotal = 0;
    pastTotal = 0;
    upcomingReservations = [];
    pastReservations = [];

    async connectedCallback() {
        this._hasConnected = true;
        await this.loadReservations();
    }

    @api
    set listingId(value) {
        const nextValue = value || null;
        if (this._listingId === nextValue) return;

        this._listingId = nextValue;
        this.resetPagination();

        if (this._hasConnected) {
            this.loadReservations();
        }
    }
    get listingId() {
        return this._listingId;
    }

    @api
    set listingName(value) {
        this._listingName = value || null;
    }
    get listingName() {
        return this._listingName;
    }

    async getIdToken() {
        if (!this._idToken) {
            const session = await fetchAuthSession();
            this._idToken = session.tokens?.idToken?.toString();
        }

        return this._idToken;
    }

    async loadReservations() {
        this._isLoading = true;

        try {
            if (IS_TESTING_NO_INTERNET) {
                const samplePayload = createSampleReservations();
                this.upcomingReservations = this.formatReservations(samplePayload.upcomingReservations);
                this.pastReservations = this.formatReservations(samplePayload.pastReservations);
                this.upcomingTotal = samplePayload.upcomingTotal;
                this.pastTotal = samplePayload.pastTotal;
                return;
            }

            const idToken = await this.getIdToken();
            if (!idToken) {
                throw new Error('No idToken (not signed in)');
            }

            const queryParams = {
                pageSize: String(this.pageSize),
                upcomingPage: String(this.upcomingPage),
                pastPage: String(this.pastPage)
            };

            if (this.listingId) {
                queryParams.listingId = this.listingId;
            }

            const op = get({
                apiName: 'api',
                path: '/cognito/host-reservations',
                options: {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                    queryParams
                },
            });

            const { body } = await op.response;
            const payload = await body.json();

            this.upcomingReservations = this.formatReservations(payload?.upcomingReservations || []);
            this.pastReservations = this.formatReservations(payload?.pastReservations || []);
            this.upcomingTotal = Number(payload?.upcomingTotal || 0);
            this.pastTotal = Number(payload?.pastTotal || 0);
        } catch (error) {
            this.showToast('Error loading reservations', error?.message || error, 'error');
        } finally {
            this._isLoading = false;
        }
    }

    formatReservations(reservations = []) {
        return reservations.map((reservation, index) => {
            const guest = reservation.app_user || reservation.appUser || {};

            return {
                id: reservation.id || `${reservation.start_time}-${index}`,
                startTimeLabel: formatDateTime(reservation.start_time || reservation.startTime),
                endTimeLabel: formatDateTime(reservation.end_time || reservation.endTime),
                guestCountLabel: formatValue(reservation.guest_count ?? reservation.guestCount),
                guestFirstNameLabel: formatValue(guest.first_name || guest.firstName),
                guestLastNameLabel: formatValue(guest.last_name || guest.lastName),
                hostPayoutLabel: formatCurrencyFromCents(
                    reservation.host_payout_cents ?? reservation.hostPayoutCents
                ),
                paymentStatusLabel: formatValue(reservation.payment_status || reservation.paymentStatus),
                refundedLabel: formatCurrencyFromCents(
                    reservation.refunded_cents ?? reservation.refundedCents
                )
            };
        });
    }

    get hasUpcomingReservations() {
        return this.upcomingReservations.length > 0;
    }

    get hasPastReservations() {
        return this.pastReservations.length > 0;
    }

    get hasListingFilter() {
        return !!this.listingId;
    }

    get pageClass() {
        return this.hasListingFilter
            ? 'host-reservations host-reservations_listing-filter'
            : 'host-reservations';
    }

    get listingFilterLabel() {
        return this.listingName
            ? `Showing reservations for ${this.listingName}`
            : 'Showing reservations for selected listing';
    }

    get pageSizeOptions() {
        return PAGE_SIZE_OPTIONS.map(option => ({
            ...option,
            isSelected: Number(option.value) === this.pageSize
        }));
    }

    get upcomingTotalPages() {
        return Math.max(1, Math.ceil(this.upcomingTotal / this.pageSize));
    }

    get pastTotalPages() {
        return Math.max(1, Math.ceil(this.pastTotal / this.pageSize));
    }

    get isUpcomingPreviousDisabled() {
        return this.upcomingPage <= 1 || this._isLoading;
    }

    get isUpcomingNextDisabled() {
        return this.upcomingPage >= this.upcomingTotalPages || this._isLoading;
    }

    get isPastPreviousDisabled() {
        return this.pastPage <= 1 || this._isLoading;
    }

    get isPastNextDisabled() {
        return this.pastPage >= this.pastTotalPages || this._isLoading;
    }

    get upcomingPageLabel() {
        return `Page ${this.upcomingPage} of ${this.upcomingTotalPages}`;
    }

    get pastPageLabel() {
        return `Page ${this.pastPage} of ${this.pastTotalPages}`;
    }

    async handlePageSizeChange(event) {
        const nextPageSize = Math.min(100, Math.max(10, Number(event.target.value) || 10));
        this.pageSize = nextPageSize;
        this.resetPagination();
        await this.loadReservations();
    }

    resetPagination() {
        this.upcomingPage = 1;
        this.pastPage = 1;
    }

    async handleUpcomingPrevious() {
        if (this.isUpcomingPreviousDisabled) return;

        this.upcomingPage -= 1;
        await this.loadReservations();
    }

    async handleUpcomingNext() {
        if (this.isUpcomingNextDisabled) return;

        this.upcomingPage += 1;
        await this.loadReservations();
    }

    async handlePastPrevious() {
        if (this.isPastPreviousDisabled) return;

        this.pastPage -= 1;
        await this.loadReservations();
    }

    async handlePastNext() {
        if (this.isPastNextDisabled) return;

        this.pastPage += 1;
        await this.loadReservations();
    }

    showToast(title, message, variant, mode = null) {
        const modeP = mode
            ? mode
            : variant === 'success'
                ? 'dismissible'
                : 'sticky';

        this.toastObj = {
            variant,
            mode: modeP,
            message,
            header: {
                text: title
            }
        };
    }
}
